/**
 * Multi-layer Caching System
 * Memory (fastest) -> localStorage (persists) -> API (with ETag)
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  etag?: string;
}

/**
 * In-memory cache with TTL support
 */
export class MemoryCache<T = unknown> {
  private cache = new Map<string, CacheEntry<T>>();
  private cleanupInterval: number | null = null;

  constructor(cleanupIntervalMs: number = 60000) {
    // Only set interval in browser environment
    if (typeof window !== 'undefined') {
      this.cleanupInterval = window.setInterval(
        () => this.cleanup(),
        cleanupIntervalMs
      );
    }
  }

  get(key: string): T | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return undefined;
    }

    return entry.value;
  }

  set(key: string, value: T, ttlMs: number, etag?: string): void {
    this.cache.set(key, {
      value,
      expiresAt: Date.now() + ttlMs,
      etag,
    });
  }

  getEntry(key: string): CacheEntry<T> | undefined {
    return this.cache.get(key);
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  invalidatePattern(pattern: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  clear(): void {
    this.cache.clear();
  }

  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  destroy(): void {
    if (this.cleanupInterval !== null) {
      clearInterval(this.cleanupInterval);
    }
    this.clear();
  }
}

/**
 * localStorage cache wrapper with TTL and ETag support
 */
export class StorageCache {
  private prefix: string;

  constructor(prefix: string = 'khaime:') {
    this.prefix = prefix;
  }

  private getKey(key: string): string {
    return this.prefix + key;
  }

  get<T>(key: string): T | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const item = localStorage.getItem(this.getKey(key));
      if (!item) return null;

      const entry: CacheEntry<T> = JSON.parse(item);
      return entry.value;
    } catch {
      return null;
    }
  }

  getEntry<T>(key: string): CacheEntry<T> | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const item = localStorage.getItem(this.getKey(key));
      if (!item) return null;
      return JSON.parse(item);
    } catch {
      return null;
    }
  }

  set<T>(key: string, value: T, ttlMs: number, etag?: string): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const entry: CacheEntry<T> = {
        value,
        expiresAt: Date.now() + ttlMs,
        etag,
      };
      localStorage.setItem(this.getKey(key), JSON.stringify(entry));
    } catch {
      // Storage full or disabled - silently fail
    }
  }

  isExpired(key: string): boolean {
    if (typeof localStorage === 'undefined') return true;

    try {
      const item = localStorage.getItem(this.getKey(key));
      if (!item) return true;

      const entry = JSON.parse(item);
      return Date.now() > entry.expiresAt;
    } catch {
      return true;
    }
  }

  delete(key: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(this.getKey(key));
  }

  invalidatePattern(pattern: string): void {
    if (typeof localStorage === 'undefined') return;

    const fullPattern = this.getKey(pattern);
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(fullPattern)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  clear(): void {
    if (typeof localStorage === 'undefined') return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

/**
 * Combined cache with memory + storage fallback
 */
export class Cache<T = unknown> {
  private memory: MemoryCache<T>;
  private storage: StorageCache;
  private pendingRequests = new Map<string, Promise<T>>();

  constructor(storagePrefix: string = 'khaime:cache:') {
    this.memory = new MemoryCache<T>();
    this.storage = new StorageCache(storagePrefix);
  }

  /**
   * Get from cache (memory first, then storage)
   */
  get(key: string): T | undefined {
    // Check memory first (fastest)
    const memoryValue = this.memory.get(key);
    if (memoryValue !== undefined) {
      return memoryValue;
    }

    // Check storage (slower but persists)
    const storageEntry = this.storage.getEntry<T>(key);
    if (storageEntry && !this.storage.isExpired(key)) {
      // Populate memory cache for subsequent reads
      const ttl = storageEntry.expiresAt - Date.now();
      this.memory.set(key, storageEntry.value, ttl, storageEntry.etag);
      return storageEntry.value;
    }

    return undefined;
  }

  /**
   * Get with ETag for conditional requests
   */
  getWithEtag(key: string): { value: T | undefined; etag?: string } {
    const memoryEntry = this.memory.getEntry(key);
    if (memoryEntry && Date.now() <= memoryEntry.expiresAt) {
      return { value: memoryEntry.value, etag: memoryEntry.etag };
    }

    const storageEntry = this.storage.getEntry<T>(key);
    if (storageEntry) {
      return { value: storageEntry.value, etag: storageEntry.etag };
    }

    return { value: undefined };
  }

  /**
   * Set in both memory and storage
   */
  set(key: string, value: T, ttlMs: number, etag?: string): void {
    this.memory.set(key, value, ttlMs, etag);
    this.storage.set(key, value, ttlMs, etag);
  }

  /**
   * Get or fetch with deduplication
   * Prevents multiple concurrent requests for the same key
   */
  async getOrFetch(
    key: string,
    fetcher: () => Promise<T>,
    ttlMs: number
  ): Promise<T> {
    // Check cache first
    const cached = this.get(key);
    if (cached !== undefined) {
      return cached;
    }

    // Check if request is already in flight
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending;
    }

    // Create new request
    const request = fetcher()
      .then((value) => {
        this.set(key, value, ttlMs);
        return value;
      })
      .finally(() => {
        this.pendingRequests.delete(key);
      });

    this.pendingRequests.set(key, request);
    return request;
  }

  /**
   * Get stale data while revalidating in background
   */
  async getStaleWhileRevalidate(
    key: string,
    fetcher: () => Promise<T>,
    ttlMs: number
  ): Promise<T> {
    const cached = this.get(key);

    // Return stale data immediately if available
    if (cached !== undefined) {
      // Revalidate in background
      this.revalidate(key, fetcher, ttlMs);
      return cached;
    }

    // No cache, must wait for fetch
    return this.getOrFetch(key, fetcher, ttlMs);
  }

  private async revalidate(
    key: string,
    fetcher: () => Promise<T>,
    ttlMs: number
  ): Promise<void> {
    // Use requestIdleCallback if available
    const doRevalidate = async () => {
      try {
        const value = await fetcher();
        this.set(key, value, ttlMs);
      } catch {
        // Silent fail - stale data is already returned
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => doRevalidate());
    } else {
      setTimeout(doRevalidate, 0);
    }
  }

  delete(key: string): void {
    this.memory.delete(key);
    this.storage.delete(key);
  }

  invalidatePattern(pattern: string): void {
    this.memory.invalidatePattern(pattern);
    this.storage.invalidatePattern(pattern);
  }

  clear(): void {
    this.memory.clear();
    this.storage.clear();
  }

  destroy(): void {
    this.memory.destroy();
  }
}

/**
 * Singleton cache instance for SDK
 */
let globalCache: Cache | null = null;

export function getCache(): Cache {
  if (!globalCache) {
    globalCache = new Cache('khaime:');
  }
  return globalCache;
}

/**
 * Cache key builders
 */
export const CacheKeys = {
  config: (merchantId: string | number, sdkType: string) =>
    `config:${merchantId}:${sdkType}`,

  bootstrap: (merchantId: string | number, sandbox: boolean) =>
    `bootstrap:${merchantId}:${sandbox ? 'sandbox' : 'live'}`,

  products: (
    merchantId: string | number,
    currency: string,
    options?: Record<string, unknown>
  ) =>
    `products:${merchantId}:${currency}:${options ? JSON.stringify(options) : ''}`,

  product: (
    merchantId: string | number,
    productId: number,
    currency: string
  ) => `product:${merchantId}:${productId}:${currency}`,
};

/**
 * Cache TTLs (in milliseconds)
 */
export const CacheTTL = {
  CONFIG: 5 * 60 * 1000, // 5 minutes (matches backend Redis)
  BOOTSTRAP: 5 * 60 * 1000, // 5 minutes
  PRODUCTS: 3 * 60 * 1000, // 3 minutes
  PRODUCT: 3 * 60 * 1000, // 3 minutes
};
